import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DataTable, DropButton, Menu, Pagination, Tip } from 'grommet';
import { Dashboard, Filter, Menu as MenuIcon, SettingsOption, Share } from 'grommet-icons';
import { ApiError, EquipmentService, TListEquipmentRequest } from '/src/api';
import {
  AddEquipmentModal,
  Box,
  BoxProps,
  Card,
  CardBody,
  CardHeader,
  DataTableHeader,
  DataTableItem,
  DeleteMultipleEquipmentModal,
  EquipmentIcon,
  Filters,
  FiltersDropdown,
  Link,
  LoadingSpinner,
  RetireMultipleEquipmentModal,
  SearchInput,
  Text,
  UpdateMultipleMeteredModal,
  UpdateMultipleUnmeteredModal,
} from '/src/components';
import { useFacilityStore, useGlobalStore, useUserStore } from '/src/context';
import { useRestorableListState, useSyncDraft } from '/src/hooks/useRestorableListState';
import { Equipment, TEquipmentId, TFacilityId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { tooltipMessages } from '/src/lib/tooltips';
import { DateString, EntityStatus, Nullable, TResponseMetadata } from '/src/lib/types';
import { formatNumber, getPeriodFromDateString, pxToRem } from '/src/utils';

type EquipmentListQueryState = {
  page: number;
  search: string;
  is_active: string | undefined; // 'true' | 'false' | '' | undefined
  is_metered: string | undefined; // 'true' | 'false' | undefined
};

const EQUIPMENT_LIST_DEFAULTS: EquipmentListQueryState = {
  page: 1,
  search: '',
  is_active: 'true',
  is_metered: undefined,
};

const EQUIPMENT_LIST_CLEARED: EquipmentListQueryState = {
  ...EQUIPMENT_LIST_DEFAULTS,
  is_active: '',
  is_metered: undefined,
};

export type EquipmentListProps = BoxProps & {
  clientId: TEquipmentId;
  facilityId: TFacilityId;
  firstReportingQuarter?: DateString;
  lastReportingQuarter?: Nullable<DateString>;
  isCanada?: boolean;
  title?: string;
  equipment?: Equipment[];
  fetchEquipment?: (page?: number) => Promise<void>;
  metadata?: TResponseMetadata;
  isLoading?: boolean;
  showAddButton?: boolean;
  showShareButton?: boolean;
  showBulkUpdateButton?: boolean;
  showFilters?: boolean;
  showSearchInput?: boolean;
  hideHeader?: boolean;
  currentPage?: number;
  setCurrentPage?: (page: number) => void;
  surfaceId?: string;
  standalone?: boolean;
  mode?: 'query' | 'controlled';
};

export const EquipmentList: React.FC<EquipmentListProps> = (props) => {
  const {
    title,
    clientId,
    facilityId,
    isCanada,
    showAddButton,
    showShareButton,
    showFilters,
    showSearchInput,
    isLoading: propIsLoading,
    equipment: propEquipment,
    fetchEquipment: propFetchEquipment,
    currentPage: propCurrentPage,
    setCurrentPage: propSetCurrentPage,
    metadata: propMetadata,
    hideHeader,
    surfaceId,
    standalone = false,
    mode = 'query',
    ...boxProps
  } = props;

  const isPropControlled = mode === 'controlled';

  /** Context **/
  const navigate = useNavigate();
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const facilityStore = useFacilityStore();
  const tooltips = tooltipMessages();
  const { t } = useTranslation();

  /** Restorable state **/
  const restorableList = useRestorableListState<EquipmentListQueryState>({
    surfaceId: surfaceId || 'equipment-list',
    defaults: EQUIPMENT_LIST_DEFAULTS,
    standalone,
  });

  const queryState = restorableList.state;
  const setQueryState = restorableList.setState;

  /** Draft state **/
  const [draftSearch, setDraftSearch] = useState(queryState.search);
  const [draftFilters, setDraftFilters] = useState<Record<string, string | undefined>>({
    is_active: queryState.is_active,
    is_metered: queryState.is_metered,
  });

  /** Modal state **/
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [showEditUnmetered, setShowEditUnmetered] = useState(false);
  const [showEditMetered, setShowEditMetered] = useState(false);
  const [showRetireMultipleModal, setShowRetireMultipleModal] = useState(false);
  const [showDeleteMultipleModal, setShowDeleteMultipleModal] = useState(false);

  /** Permission state **/
  const [userCanRetire, setUserCanRetire] = useState(false);
  const [userCanDelete, setUserCanDelete] = useState(false);
  const [hasMeteredEquipment, setHasMeteredEquipment] = useState(false);

  /** UI state **/
  const [isFiltersMenuOpen, setIsFiltersMenuOpen] = useState(false);
  const [isFiltersMenuHovered, setIsFiltersMenuHovered] = useState(false);
  const [isMenuOpen] = useState(false);
  const [isMenuHovered, setIsMenuHovered] = useState(false);

  const request: TListEquipmentRequest = useMemo(
    () => ({
      facility_id: facilityId,
      page: queryState.page,
      limit: 20,
      serial_number: queryState.search || undefined,
      is_active: queryState.is_active === 'true' ? true : queryState.is_active === 'false' ? false : undefined,
      is_metered: queryState.is_metered === 'true' ? true : queryState.is_metered === 'false' ? false : undefined,
    }),
    [queryState, facilityId]
  );

  /** React Query **/
  const {
    data: queryData,
    isLoading: queryIsLoading,
    isFetching,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['equipment', userStore.user?.id, request],
    queryFn: () => EquipmentService.list(request),
    enabled: !isPropControlled && !!userStore.user,
    placeholderData: (prev) => prev,
  });

  const queryEquipment = queryData?.data;
  const queryMetadata = queryData?.meta;

  /** Show toast on query error **/
  useEffect(() => {
    if (queryError && !isPropControlled) {
      globalStore.handleApiError(queryError as ApiError, toastMessages.listEquipment.error);
    }
  }, [queryError]);

  /** Fetch facility metadata **/
  useEffect(() => {
    if (isPropControlled || !facilityId || Number.isNaN(facilityId)) return;
    facilityStore.fetchFacility(facilityId);
  }, [facilityId, isPropControlled]);

  /** Check equipment counts for menu permissions **/
  useEffect(() => {
    if (isPropControlled || !facilityId || Number.isNaN(facilityId)) return;

    let cancelled = false;
    const setIfCurrent = (setter: (value: boolean) => void) => (value: boolean) => {
      if (!cancelled) setter(value);
    };

    const hasMatches = (filter: Partial<TListEquipmentRequest>) =>
      EquipmentService.list({ facility_id: facilityId, limit: 1, page: 1, ...filter })
        .then((equipmentList) => (equipmentList.meta?.total ?? 0) > 0)
        .catch(() => false);

    const hasRetireAccess =
      userStore.isFuseAdmin ||
      ((userStore.isClientAdmin || userStore.isFacilityAdmin) &&
        (userStore.user?.facilities.some((f) => f.id === facilityId) ?? false));

    if (hasRetireAccess) {
      hasMatches({ is_active: true }).then(setIfCurrent(setUserCanRetire));
    } else {
      setUserCanRetire(false);
    }

    if (userStore.isFuseAdmin) {
      hasMatches({ is_active: true, is_finalized: false }).then(setIfCurrent(setUserCanDelete));
    } else {
      setUserCanDelete(false);
    }

    hasMatches({ is_metered: true }).then(setIfCurrent(setHasMeteredEquipment));

    return () => {
      cancelled = true;
    };
  }, [facilityId, isPropControlled, userStore.isFuseAdmin, userStore.isClientAdmin, userStore.isFacilityAdmin]);

  /** Resolved values **/
  const _equipment = propEquipment ?? queryEquipment;
  const _metadata = propMetadata ?? queryMetadata;
  const _currentPage = propCurrentPage ?? queryState.page;
  const _isLoading = propIsLoading ?? (isPropControlled ? false : queryIsLoading || isFetching);
  const isRefetching = isFetching && !queryIsLoading;
  const hasEquipment = !!_equipment?.length;

  const noResultsMessage = useMemo(() => {
    const activeMessage =
      queryState.is_active === 'true' ? 'active' : queryState.is_active === 'false' ? 'inactive' : '';
    const meteredMessage =
      queryState.is_metered === 'true' ? 'metered' : queryState.is_metered === 'false' ? 'non-metered' : '';
    return `No ${activeMessage} ${meteredMessage} equipment found.`;
  }, [queryState.is_active, queryState.is_metered]);

  /** Methods **/
  const search = useCallback(
    (query?: string) => {
      setQueryState({ search: query || '', page: 1 });
    },
    [setQueryState]
  );

  const applyFilters = useCallback(() => {
    setQueryState({
      is_active: draftFilters.is_active,
      is_metered: draftFilters.is_metered,
      page: 1,
    });
    setIsFiltersMenuOpen(false);
  }, [setQueryState, draftFilters]);

  const clearFilters = useCallback(() => {
    setDraftFilters({
      is_active: EQUIPMENT_LIST_CLEARED.is_active,
      is_metered: EQUIPMENT_LIST_CLEARED.is_metered,
    });
    setDraftSearch('');
    setQueryState(EQUIPMENT_LIST_CLEARED);
    setIsFiltersMenuOpen(false);
  }, [setQueryState]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (isPropControlled) {
        propSetCurrentPage?.(page);
        propFetchEquipment?.(page);
      } else {
        setQueryState({ page });
      }
    },
    [isPropControlled, propSetCurrentPage, propFetchEquipment, setQueryState]
  );

  /** shouldRefresh handler */
  const handleShouldRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  /** Sync page from API response **/
  useEffect(() => {
    if (queryMetadata && !isPropControlled && queryMetadata.current_page !== queryState.page) {
      setQueryState({ page: queryMetadata.current_page });
    }
  }, [queryMetadata?.current_page]);

  /** Sync drafts when queryState changes **/
  useSyncDraft(queryState, setDraftSearch, (s) => s.search);
  useSyncDraft(queryState, setDraftFilters, (s) => ({
    is_active: s.is_active,
    is_metered: s.is_metered,
  }));

  /** Render **/
  return (
    <Box fill="horizontal">
      <Box row gap="medium">
        <Card {...boxProps}>
          {!hideHeader && (
            <CardHeader
              title={title || t('equipment:equipmentList')}
              icon={<EquipmentIcon size="24" />}
              {...(showShareButton && {
                button: <Share size="17px" />,
                buttonNavigatePath: `/clients/${clientId}/facilities/${facilityId}/equipment`,
                buttonTooltip: tooltipMessages().equipmentPageLink,
              })}
            >
              <Box direction="row" gap="small">
                {showSearchInput && (
                  <Box data-tour="equipment-search">
                    <SearchInput
                      searchQuery={draftSearch}
                      placeholder={t('equipment:searchBySerial')}
                      setSearchQuery={setDraftSearch}
                      onSearch={search}
                      isSearching={isRefetching}
                    />
                  </Box>
                )}
                <DropButton
                  plain
                  icon={<Filter color={isFiltersMenuHovered || isFiltersMenuOpen ? 'brand' : undefined} />}
                  open={isFiltersMenuOpen}
                  onOpen={() => setIsFiltersMenuOpen(true)}
                  onClose={() => setIsFiltersMenuOpen(false)}
                  onMouseOver={() => setIsFiltersMenuHovered(true)}
                  onMouseOut={() => setIsFiltersMenuHovered(false)}
                  tip={tooltips.filterEquipment}
                  dropAlign={{ right: 'right', top: 'bottom' }}
                  dropContent={
                    <FiltersDropdown
                      isFiltering={isRefetching}
                      onSubmit={() => applyFilters()}
                      onClear={() => clearFilters()}
                      filters={[
                        {
                          label: t('equipment:activityStatus'),
                          value: draftFilters.is_active ?? '',
                          setValue: (value) =>
                            setDraftFilters({ ...draftFilters, is_active: (value as string) || undefined }),
                          options: [
                            { label: t('common:all'), value: '' },
                            { label: t('common:active'), value: 'true' },
                            { label: t('common:retired'), value: 'false' },
                          ],
                        },
                        {
                          label: t('equipment:meterType'),
                          value: draftFilters.is_metered ?? '',
                          setValue: (value) =>
                            setDraftFilters({ ...draftFilters, is_metered: (value as string) || undefined }),
                          options: [
                            { label: t('common:all'), value: '' },
                            { label: t('equipment:metered'), value: 'true' },
                            { label: t('equipment:unmetered'), value: 'false' },
                          ],
                        },
                      ]}
                    />
                  }
                />
                {facilityStore.facility?.is_active && (
                  <Box data-tour="equipment-actions-button">
                    <Menu
                      icon={<MenuIcon color={isMenuHovered || isMenuOpen ? 'brand' : undefined} />}
                      onMouseOver={() => setIsMenuHovered(true)}
                      onMouseOut={() => setIsMenuHovered(false)}
                      tip={tooltips.equipmentActions}
                      dropAlign={{ top: 'bottom', right: 'right' }}
                      items={[
                        {
                          label: t('equipment:editMetered'),
                          onClick: () => hasMeteredEquipment && setShowEditMetered(true),
                          disabled: !hasMeteredEquipment,
                        },
                        {
                          label: t('equipment:editUnmetered'),
                          onClick: () => !isCanada && setShowEditUnmetered(true),
                          disabled: isCanada,
                        },
                        {
                          label: t('equipment:addEquipment'),
                          onClick: () =>
                            facilityId
                              ? setShowEquipmentModal(true)
                              : navigate(`/clients/${clientId}/equipment/create`),
                          'data-tour': 'add-equipment-button',
                        },
                        {
                          label: t('equipment:retireEquipment'),
                          onClick: () => userCanRetire && setShowRetireMultipleModal(true),
                          disabled: !userCanRetire,
                        },
                        {
                          label: t('equipment:deleteEquipment'),
                          onClick: () => userCanDelete && setShowDeleteMultipleModal(true),
                          disabled: !userCanDelete,
                        },
                      ]
                        .filter((item) => item.label !== t('equipment:addEquipment') || showAddButton)
                        .filter((item) => item.label !== t('equipment:deleteEquipment') || userCanDelete)
                        .filter((item) => item.label !== t('equipment:retireEquipment') || userCanRetire)
                        .filter((item) => item.label !== t('equipment:editMetered') || hasMeteredEquipment)
                        .filter((item) => item.label !== t('equipment:editUnmetered') || !isCanada)}
                    />
                  </Box>
                )}
              </Box>
            </CardHeader>
          )}
          <CardBody pad="none" gap="none">
            <Box elevation="small">
              {_isLoading && <LoadingSpinner />}
              {!_isLoading && (
                <DataTable
                  id="equipment_list_data_table"
                  pad={{ horizontal: '1.5rem' }}
                  columns={[
                    {
                      property: 'id',
                      primary: true,
                      size: '4%',
                      render: (equipment) => (
                        <Box pad={{ vertical: pxToRem(23) }}>
                          <Link to={`/clients/${equipment.client_id}/equipment/${equipment.id}`}>
                            <Box row borderRadius="6px" justify="center">
                              <Tip content={tooltips.viewEquipmentDetails}>
                                <SettingsOption size="16px" color="accent-1" />
                              </Tip>
                            </Box>
                          </Link>
                        </Box>
                      ),
                    },
                    {
                      property: 'equipment_type.name',
                      size: '17%',
                      header: <DataTableHeader title={t('equipment:chargingType')} />,
                      render: (equipment) => <DataTableItem value={equipment.equipment_type.name} />,
                    },
                    {
                      property: 'is_active',
                      align: 'center',
                      header: <DataTableHeader title={t('common:status')} />,
                      render: (equipment) => (
                        <DataTableItem value={equipment.is_active ? EntityStatus.Active : EntityStatus.Retired} />
                      ),
                    },
                    {
                      property: 'is_metered',
                      size: '8%',
                      align: 'center',
                      header: <DataTableHeader title={t('equipment:meter')} />,
                      render: (equipment) => (
                        <Box row>{(equipment.is_metered && <Dashboard size="20px" />) || '-'}</Box>
                      ),
                    },
                    {
                      property: 'serial_number',
                      size: '13%',
                      align: 'center',
                      header: <DataTableHeader title={t('equipment:serialVin')} />,
                      render: (equipment) => <DataTableItem value={equipment.serial_number || '-'} />,
                    },
                    {
                      property: 'manufacturer',
                      size: '12%',
                      align: 'center',
                      header: <DataTableHeader title={t('equipment:manufacturer')} />,
                      render: (equipment) => <DataTableItem value={equipment.manufacturer || '-'} />,
                    },
                    {
                      property: 'model_year',
                      size: '11%',
                      align: 'center',
                      header: <DataTableHeader title={t('equipment:modelYear')} />,
                      render: (equipment) => <DataTableItem value={equipment.model_year || '-'} />,
                    },
                    {
                      property: 'model_number',
                      size: '13%',
                      align: 'center',
                      header: <DataTableHeader title={t('equipment:modelNumber')} />,
                      render: (equipment) => <DataTableItem value={equipment.model_number || '-'} />,
                    },
                    {
                      property: 'unit_number',
                      size: '11%',
                      align: 'center',
                      header: <DataTableHeader title={t('equipment:unitNo')} />,
                      render: (equipment) => <DataTableItem value={equipment.unit_number || '-'} />,
                    },
                    {
                      property: 'total_kwh',
                      size: '12%',
                      header: (
                        <DataTableHeader>
                          <Box align="center">
                            <Text size="small">{t('common:totalKwh')}</Text>
                            {_equipment?.[0]?.current_reporting_period_equipment_usage?.start_reporting_quarter && (
                              <Text size="small">
                                (
                                {getPeriodFromDateString(
                                  _equipment[0].current_reporting_period_equipment_usage.start_reporting_quarter,
                                  !!isCanada
                                )}
                                )
                              </Text>
                            )}
                          </Box>
                        </DataTableHeader>
                      ),
                      render: (equipment) =>
                        equipment.current_reporting_period_equipment_usage?.total_kwh ? (
                          <DataTableItem
                            value={formatNumber(
                              equipment.current_reporting_period_equipment_usage.total_kwh,
                              true,
                              false,
                              false,
                              4
                            )}
                            unitName="kWh"
                          />
                        ) : (
                          <DataTableItem value="-" />
                        ),
                    },
                  ]}
                  data={_equipment}
                  sortable
                  background={['light-6', 'white']}
                  border={{ color: 'light-2', side: 'bottom', size: 'small' }}
                  // border={{size: '0.5px'}}
                />
              )}
              {!hasEquipment && !_isLoading && (
                <Box pad={{ horizontal: '1.5rem', vertical: '2rem' }} background="light-6" justify="center">
                  <Text alignSelf="center" size="medium" fontFamily="Lato, sans-serif">
                    {noResultsMessage}
                  </Text>
                </Box>
              )}
            </Box>
            {hasEquipment && !!_metadata && !!_currentPage && (
              <Box pad="1.5rem" flex="grow">
                <Pagination
                  alignSelf="center"
                  size="small"
                  page={_currentPage}
                  step={_metadata?.per_page}
                  numberItems={_metadata?.total}
                  onChange={(e) => handlePageChange(e.page)}
                />
              </Box>
            )}
          </CardBody>
        </Card>
        {showFilters && (
          <Filters
            isFiltering={isRefetching}
            onSubmit={() => applyFilters()}
            onClear={() => clearFilters()}
            filters={[]}
          />
        )}
      </Box>
      {!!clientId && !!facilityId && (
        <AddEquipmentModal
          clientId={clientId}
          facilityId={facilityId}
          isVisible={showEquipmentModal}
          setIsVisible={setShowEquipmentModal}
        />
      )}
      {showEditUnmetered && !!clientId && !!facilityStore.facility && (
        <UpdateMultipleUnmeteredModal
          facility={facilityStore.facility}
          isVisible={showEditUnmetered}
          setIsVisible={setShowEditUnmetered}
          refreshData={handleShouldRefresh}
        />
      )}
      {showEditMetered && !!facilityStore.facility && (
        <UpdateMultipleMeteredModal
          facility={facilityStore.facility}
          isVisible={showEditMetered}
          setIsVisible={setShowEditMetered}
          setShouldRefresh={(_shouldRefresh) => {
            if (_shouldRefresh) refetch();
          }}
        />
      )}
      {showRetireMultipleModal && !!facilityStore.facility && (
        <RetireMultipleEquipmentModal
          facility={facilityStore.facility}
          isVisible={showRetireMultipleModal}
          setIsVisible={setShowRetireMultipleModal}
          setShouldRefresh={(_shouldRefresh) => {
            if (_shouldRefresh) refetch();
          }}
          isCanada={isCanada}
        />
      )}
      {showDeleteMultipleModal && !!facilityStore.facility && (
        <DeleteMultipleEquipmentModal
          facility={facilityStore.facility}
          isVisible={showDeleteMultipleModal}
          setIsVisible={setShowDeleteMultipleModal}
          setShouldRefresh={(_shouldRefresh) => {
            if (_shouldRefresh) refetch();
          }}
          isCanada={isCanada}
        />
      )}
    </Box>
  );
};
